import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Outlet } from 'react-router';
import styled, { createGlobalStyle } from 'styled-components';
import { Popup } from '../utils/popups';
import { Helmet } from './Helmet';
/**
 * Keeps popups visible while an element is fullscreen. Popups render into
 * toasterhea's portal target under document.body, but the browser only
 * paints the fullscreened element's subtree — so on Android, where the
 * mobile view goes fullscreen via element.requestFullscreen(), the
 * "Request to speak" and "Leave stage" dialogs (and the device pickers)
 * were invisible. Reparent the portal target into the fullscreen element
 * for the duration of fullscreen and move it back to <body> afterwards,
 * where toasterhea's own unmount cleanup expects to find it.
 * `.hi_from_toasterhea` is the class toasterhea gives the div it appends
 * to document.body.
 */
function useKeepPopupsVisibleInFullscreen() {
    useEffect(() => {
        const doc = document;
        const sync = () => {
            const target = doc.fullscreenElement ??
                doc.webkitFullscreenElement ??
                document.body;
            for (const container of document.querySelectorAll('.hi_from_toasterhea')) {
                if (container.parentElement !== target) {
                    target.appendChild(container);
                }
            }
        };
        document.addEventListener('fullscreenchange', sync);
        document.addEventListener('webkitfullscreenchange', sync);
        return () => {
            document.removeEventListener('fullscreenchange', sync);
            document.removeEventListener('webkitfullscreenchange', sync);
            for (const container of document.querySelectorAll('.hi_from_toasterhea')) {
                if (container.parentElement !== document.body) {
                    document.body.appendChild(container);
                }
            }
        };
    }, []);
}
export function Layout(props) {
    const { children = _jsx(Outlet, {}) } = props;
    useKeepPopupsVisibleInFullscreen();
    return (_jsxs(_Fragment, { children: [_jsx(Helmet, {}), _jsx(GlobalStyle, {}), _jsxs(LayoutRoot, { children: [_jsx("main", { children: children }), _jsx(Footer, {})] }), _jsx(Container, {})] }));
}
const Container = styled(Popup.Container) `
    height: 100%;
    left: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    width: 100%;
    /* Popups are the topmost layer. Must also beat the mobile HUD
       (z-index: 10) when this container is reparented into the
       fullscreened view element. */
    z-index: 100;
`;
const GlobalStyle = createGlobalStyle `
    html, body {
        background: var(--Layout_Background, url('/background.jpg'));
        background-attachment: fixed;
        background-size: cover;
        overscroll-behavior: none;
    }

    body {
        line-height: 2em;
    }
`;
const Footer = styled.footer `
    position: fixed;
    bottom: 1rem;
    right: 1rem;
`;
const LayoutRoot = styled.div ``;
